/**
 * MoonStepFilledView
 *
 * Compact summary of completed step data. EMBER-styled.
 * Ported from olly's StepFilledView.
 */

import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { Check } from "lucide-react-native";
import type {
  TreatmentStepProgress,
  LocalMedication,
} from "@louise/care/treatment-phases";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { EMBER } from "../../../../../onboarding/theme";
import MedRow from "../../rows/MedRow";
import {
  deriveRecurrence,
  type MedForm,
  type MedicationScheduleItem,
} from "../../schedule.types";
import { getMedicationByBrandName } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";

// Map intakeType → form icon
const INTAKE_TO_FORM: Record<string, MedForm> = {
  subcutaneous: "vial",
  intramuscular: "vial",
  intravenous: "vial",
  injection: "vial",
  oral: "pill",
  vaginal: "suppository",
  topical: "suppository",
  patch: "pill",
};

// Map content-database form names → MedForm (fallback when intakeType is unknown)
const CONTENT_FORM_MAP: Record<string, MedForm> = {
  pen: "pen",
  vial: "vial",
  multi_vial: "vial",
  pre_filled_pen: "pen",
  pre_filled_syringe: "vial",
  injection_solution: "vial",
  lyophilized_powder: "vial",
  nasal_spray: "vial",
  pill: "pill",
  tablet: "pill",
  capsule: "pill",
  oral_solution: "pill",
  sublingual_tablet: "pill",
  transdermal_patch: "pill",
  patch: "pill",
  cream: "pill",
  other: "pill",
  suppository: "suppository",
  vaginal_gel: "suppository",
  vaginal_suppository: "suppository",
};

function resolveFormFromContentDb(name: string): MedForm | undefined {
  const content = getMedicationByBrandName(name);
  if (!content) return undefined;
  const firstForm = (content.body as { commonForms?: string[] })
    .commonForms?.[0];
  return firstForm ? (CONTENT_FORM_MAP[firstForm] ?? undefined) : undefined;
}

function localMedToScheduleItem(m: LocalMedication): MedicationScheduleItem {
  const dose = m.dose != null ? `${m.dose} ${m.doseUnit ?? ""}`.trim() : "";
  const intakeType = m.intakeType?.toLowerCase() ?? "";
  const form: MedForm =
    INTAKE_TO_FORM[intakeType] ?? resolveFormFromContentDb(m.name) ?? "pill";
  const time = m.timeTaken?.[0] ?? "09:00";
  const hour = parseInt(time);
  const timing: "morning" | "evening" | "as-needed" =
    hour >= 12 ? "evening" : "morning";

  return {
    type: "medication",
    id: m.id,
    name: m.name || "Unknown",
    dosage: dose,
    form,
    route: intakeType
      ? intakeType.charAt(0).toUpperCase() + intakeType.slice(1)
      : "",
    time,
    timing,
    color: EMBER.colors.textMid,
    recurrence: deriveRecurrence({
      startDate: m.startDate,
      endDate: m.endDate,
    }),
    brandName: m.name || undefined,
    userContext: {
      dosage: dose || undefined,
      route: intakeType || undefined,
      startDate: m.startDate ?? undefined,
      endDate: m.endDate,
      notes: m.notes ?? undefined,
    },
  };
}

interface MoonStepFilledViewProps {
  step: TreatmentStepConfig;
  stepProgress: TreatmentStepProgress;
  linkedMedications: LocalMedication[];
  onEditMedication?: (med: MedicationScheduleItem) => void;
}

/**
 * The human label for a step field's option value.
 *
 * These arrive as the API's own enum — `partner`, `fresh` — and this summary was
 * pushing them straight in, so a French reader got "partner · fresh" from a card
 * whose own modal says "Partenaire · Frais". `care.treatment_flows.options` is
 * the namespace that modal reads, so the two now agree.
 *
 * Falls back to the raw value rather than trusting the lookup: `t` returns the
 * key path when an entry is missing, and an untranslated enum is a better thing
 * to put in front of a patient than `care.treatment_flows.options.something`.
 */
function optionLabel(
  value: string | number | boolean,
  t: ReturnType<typeof useTranslations>,
): string {
  const raw = String(value);
  const key = `care.treatment_flows.options.${raw}`;
  const label = t(key);
  return label === key ? raw : label;
}

function formatDate(
  isoString: string | null | undefined,
  locale: string,
): string | null {
  if (!isoString || typeof isoString !== "string") return null;
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return null;
    const dateStr = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
    if (
      isoString.includes("T") &&
      (d.getHours() !== 0 || d.getMinutes() !== 0)
    ) {
      const timeStr = new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(d);
      return `${dateStr} ${timeStr}`;
    }
    return dateStr;
  } catch {
    return null;
  }
}

function formatDateTime(
  isoString: string | null | undefined,
  locale: string,
): string | null {
  if (!isoString || typeof isoString !== "string") return null;
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return null;
    const dateStr = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
    }).format(d);
    const timeStr = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    return `${dateStr} ${timeStr}`;
  } catch {
    return null;
  }
}

function renderProcedureContent(
  step: TreatmentStepConfig,
  data: Record<string, string | number | boolean | null>,
  t: ReturnType<typeof useTranslations>,
  locale: string,
): string {
  const parts: string[] = [];
  const stepId = step.id;

  if (stepId === "egg_retrieval") {
    const eggs = data.eggsCollected;
    const date = formatDate(data.procedureDate as string, locale);
    const notes = data.notes;
    const mature = data.matureEggs;
    const frozen = data.eggsFrozen;
    if (eggs != null) {
      parts.push(
        t("care.treatment_hub.step_filled.eggs_collected", {
          params: { count: String(eggs) },
        }),
      );
    }
    if (mature != null) {
      parts.push(
        t("care.treatment_hub.step_filled.mature_count", {
          params: { count: String(mature) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (date && parts.length === 0) parts.push(date);
    else if (date) parts.push(date);
    if (notes) parts.push(String(notes));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "freezing") {
    const mature = data.matureEggs;
    const frozen = data.eggsFrozen;
    const location = data.storageLocation;
    if (mature != null) {
      parts.push(
        t("care.treatment_hub.step_filled.mature_count", {
          params: { count: String(mature) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (location) parts.push(String(location));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "fertilization") {
    const fertilized = data.eggsFertilized;
    const method = data.fertilizationMethod;
    const embryosFrozen = data.embryosFrozen;
    const notes = data.notes;
    if (fertilized != null) {
      parts.push(
        t("care.treatment_hub.step_filled.fertilized_count", {
          params: { count: String(fertilized) },
        }),
      );
    }
    if (method) parts.push(String(method).toUpperCase());
    if (embryosFrozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(embryosFrozen) },
        }),
      );
    }
    if (notes) parts.push(String(notes));
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "embryo_development") {
    const grade = data.embryoGrade;
    const frozen = data.embryosFrozen;
    if (grade) {
      parts.push(
        t("care.treatment_hub.step_filled.grade_label", {
          params: { grade: String(grade) },
        }),
      );
    }
    if (frozen != null) {
      parts.push(
        t("care.treatment_hub.step_filled.frozen_count", {
          params: { count: String(frozen) },
        }),
      );
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "transfer") {
    const transferred = data.embryosTransferred;
    const date = formatDate(data.transferDate as string, locale);
    if (transferred != null) {
      parts.push(
        t(
          transferred === 1
            ? "care.treatment_hub.step_filled.embryo_transferred_one"
            : "care.treatment_hub.step_filled.embryo_transferred_other",
          { params: { count: String(transferred) } },
        ),
      );
    }
    if (date) parts.push(date);
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "sperm_collection") {
    const source = data.spermSource;
    const type = data.spermType;
    // The date the sample was actually given. It round-trips already — the step
    // field writes `spermiograms[0].date`/`.time` and `usePhases` reads it back
    // into `collectionDate` — but this summary never read it, so recording it
    // changed nothing on the card beyond the phase's own start date. Every other
    // procedure step names its date here; this one is the omission.
    const date = formatDate(data.collectionDate as string, locale);
    if (source) parts.push(optionLabel(source, t));
    if (type) parts.push(optionLabel(type, t));
    if (date) parts.push(date);
    if (data.semenSpecimenId) {
      parts.push(t("care.treatment_hub.step_filled.specimen_linked"));
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (stepId === "insemination") {
    const date = formatDate(data.procedureDate as string, locale);
    if (date) parts.push(t("care.treatment_hub.step_filled.iui_completed"));
    if (date) parts.push(date);
    if (data.semenSpecimenId) {
      parts.push(t("care.treatment_hub.step_filled.specimen_linked"));
    }
    if (parts.length > 0) return parts.join(" \u00B7 ");
  }

  if (step.fields) {
    for (const field of step.fields) {
      const val = data[field.name];
      if (val == null || val === "" || field.type === "textarea") continue;
      const customVal = data[`${field.name}Custom`];
      const display =
        val === "Other" && customVal != null && customVal !== ""
          ? customVal
          : val;
      parts.push(`${field.label}: ${display}`);
    }
  }

  return parts.length > 0
    ? parts.join(" \u00B7 ")
    : t("care.treatment_hub.step_filled.completed");
}

function renderTriggerContent(
  data: Record<string, string | number | boolean | null>,
  t: ReturnType<typeof useTranslations>,
  locale: string,
): string {
  const triggerTime = data.triggerTime as string | null;
  const triggerDate = data.triggerDate as string | null;
  const med1 =
    (data.triggerMedicationCustom as string | null) ||
    (data.triggerMedication as string | null) ||
    null;
  const med2Raw = data.triggerMedication2 as string | null;
  const med2 =
    (data.triggerMedicationCustom2 as string | null) ||
    (med2Raw && med2Raw !== "__pending__" ? med2Raw : null) ||
    null;
  const triggerMed = med1 && med2 ? `${med1} + ${med2}` : med1 || null;

  const parts: string[] = [];
  if (triggerMed) parts.push(triggerMed);
  if (triggerTime || triggerDate) {
    const dateStr = formatDateTime(triggerDate ?? triggerTime, locale);
    if (dateStr) parts.push(dateStr);
  }

  if (parts.length > 0) {
    return `${t("care.treatment_hub.step_filled.trigger_confirmed")} \u00B7 ${parts.join(" \u00B7 ")}`;
  }

  return t("care.treatment_hub.step_filled.trigger_confirmed");
}

export function MoonStepFilledView({
  step,
  stepProgress,
  linkedMedications,
  onEditMedication,
}: MoonStepFilledViewProps) {
  const t = useTranslations();
  const { locale } = useLocale();
  const data = stepProgress.data ?? {};

  let summaryText: string;

  switch (step.contentType) {
    case "medications":
      summaryText =
        linkedMedications.length > 0
          ? t(
              linkedMedications.length === 1
                ? "care.treatment_hub.step_filled.medications_one"
                : "care.treatment_hub.step_filled.medications_other",
              { params: { count: String(linkedMedications.length) } },
            )
          : t("care.treatment_hub.step_filled.completed");
      break;
    case "procedure":
      summaryText = renderProcedureContent(step, data, t, locale);
      break;
    case "trigger":
      summaryText = renderTriggerContent(data, t, locale);
      break;
    case "waiting":
      summaryText = t("care.treatment_hub.step_filled.waiting_in_progress");
      break;
    default:
      summaryText = t("care.treatment_hub.step_filled.completed");
  }

  if (step.contentType === "medications" && linkedMedications.length > 0) {
    return (
      <View style={{ gap: 0, marginLeft: -16 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            color: EMBER.colors.textFaint,
            marginLeft: 16,
            marginBottom: 4,
          }}
        >
          {t(
            linkedMedications.length === 1
              ? "care.treatment_hub.step_filled.medications_one"
              : "care.treatment_hub.step_filled.medications_other",
            { params: { count: String(linkedMedications.length) } },
          )}
        </Text>
        {linkedMedications.slice(0, 4).map((m, i) => (
          <View
            key={m.id}
            style={{
              marginTop: i > 0 ? 2 : 0,
            }}
          >
            <MedRow
              item={localMedToScheduleItem(m)}
              onEditMedication={onEditMedication}
            />
          </View>
        ))}
        {linkedMedications.length > 4 && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              color: EMBER.colors.textFaint,
              marginLeft: 16,
              marginTop: 4,
            }}
          >
            {t("care.treatment_hub.completion.med_more_suffix", {
              params: { count: String(linkedMedications.length - 4) },
            })}
          </Text>
        )}
      </View>
    );
  }

  return (
    <View className="flex-row items-center gap-1.5">
      <Check size={12} color={EMBER.colors.textLight} strokeWidth={2} />
      <Text
        className="font-dm-sans"
        style={{ fontSize: 12, color: EMBER.colors.textLight, flex: 1 }}
        numberOfLines={2}
      >
        {summaryText}
      </Text>
    </View>
  );
}
